import React from 'react';
import _ from 'lodash';
import Isvg from 'react-inlinesvg';
import { Badge } from 'reactstrap';
import Header from 'app/components/layout/Header';
import CopyCaption from 'app/components/general/CopyCaption';
import ConnectionGroup from 'app/components/general/ConnectionGroup';
import SectionGroup from 'app/components/general/SectionGroup';

import { simpleDateTwo, parseAmount } from 'app/utils/parsers';
import { IGroup } from 'app/ducks/groups/types';

import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import MatchPaymentsTable from 'app/components/payments/MatchPaymentsTable';
import Loader from 'app/components/general/Loader';
import PaymentBadge from 'app/components/general/PaymentBadge';

import * as groupsActions from 'app/ducks/groups/actions';
import * as matchPaymentActions from 'app/ducks/matchPayments/actions';

import * as loadingSelectors from 'app/ducks/loading/selectors';
import * as groupsSelectors from 'app/ducks/groups/selectors';
import * as matchPaymentsSelectors from 'app/ducks/matchPayments/selectors';

import CompetitionIcon from 'app/images/icons/organisation.svg';

function selector(state) {
  return {
    matchPayments: matchPaymentsSelectors.getMatchPayments(state),
    groups: groupsSelectors.getGroups(state),
    appReady: loadingSelectors.appReady(state),
  };
}

const actionCreators = _.merge(
  groupsActions,
  matchPaymentActions,
);

interface ITeamDetailProps {
  appReady: boolean;
  actions: any;
  match: any;
  groups: any;
  matchPayments?: any;
  backendErrors: any;
  errors: any;
  isLoading: boolean;
}
@connect(selector, (dispatch) => ({
  actions: bindActionCreators(actionCreators, dispatch),
}))
export default class TeamDetail extends React.Component<ITeamDetailProps> {
  static defaultProps: ITeamDetailProps = {
    appReady: false,
    actions: undefined,
    match: undefined,
    groups: undefined,
    matchPayments: undefined,
    backendErrors: undefined,
    isLoading: false,
    errors: undefined,
  };

  componentDidMount = (): void => {
    this.props.actions.clearMatchPayments();
    this.props.actions.getGroup(this.props.match.params.teamId);
  };

  render(): React.ReactNode {
    const group: IGroup = this.props.groups.currentGroup;
    const isLoading = this.props.groups.isLoading && group.id !== this.props.match.params.teamId;
    const financialStatusHumanised = groupsSelectors.getFinancialStatusHumanised(group);
    return (
      <div>
        <div className="row">
          <div className="col">
            <Header
              title={group.name}
              isLoading={isLoading}
              softLoading={this.props.groups.isLoading}
              badge={(
                <PaymentBadge
                  className="event-card-badge"
                  paymentText={financialStatusHumanised}
                />
              )}
              descriptors={
                [
                  {
                    icon: <Isvg src={CompetitionIcon} />,
                    descriptor: group.competitionName,
                  },
                ]
              }
              metadata={[
                {
                  title: 'Since',
                  value: group.createdAt !== undefined ? simpleDateTwo(group.createdAt) : '',
                },
                {
                  title: 'Type',
                  value: 'Team',
                },
                {
                  title: 'Team Contacts',
                  value: group.peopleTotalCount?.toString(),
                },
                {
                  title: 'Forfeits',
                  value: group.forfeitsTotalCount?.toString(),
                },
                {
                  title: 'Matches',
                  value: group.fixtureMatchesTotalCount?.toString(),
                },
                {
                  title: 'Paid-to-date',
                  value: parseAmount(group.paidTotalPaymentsTotalAmount),
                },
                {
                  title: 'Owing',
                  value: parseAmount(group.pendingTotalPaymentsTotalAmount + group.overdueTotalPaymentsTotalAmount),
                },
              ]}
            />
          </div>
        </div>
        <div className="main-content">
          <div className="row">
            <div className="col col-12 col-lg-8 col-xlg-6">
              <SectionGroup title="Details" isLoading={isLoading}>
                <div className="row">
                  <div className="col col-4 text-grey">
                    Team Name
                  </div>
                  <div className="col">
                    {group.name}
                  </div>
                </div>
                <div className="row with-margin-top-10">
                  <div className="col col-4 text-grey">
                    Unique Key
                  </div>
                  <div className="col">
                    <CopyCaption text={group.uniqueCode} />
                  </div>
                </div>
                <div className="row with-margin-top-10">
                  <div className="col col-4 text-grey">
                    Competition/s
                  </div>
                  <div className="col">
                    {group.competitionName}
                  </div>
                </div>
                <div className="row with-margin-top-10">
                  <div className="col col-4 text-grey">
                    Section/s
                  </div>
                  <div className="col">
                    {group.sectionName}
                  </div>
                </div>
                <div className="row with-margin-top-10">
                  <div className="col col-4 text-grey">
                    Season/s
                  </div>
                  <div className="col">
                    {group.seasonName}
                  </div>
                </div>
                <div className="row with-margin-top-10">
                  <div className="col col-4 text-grey">
                    Internal Identifier
                  </div>
                  <div className="col">
                    <CopyCaption text={group.id} />
                  </div>
                </div>
              </SectionGroup>
            </div>
          </div>
          <div className="row with-margin-top-extra">
            <div className="col col-12 col-lg-8 col-xlg-6">
              <ConnectionGroup
                title="Connections"
                isLoading={isLoading}
                items={
                  [
                    {
                      type: 'customer',
                      title: 'Customers',
                      links: _.map(group.people, (person) => ({
                        name: person.fullName,
                        id: person.id,
                      })),
                    },
                    {
                      type: 'competition',
                      title: 'Competition',
                      links: [{
                        name: group.competitionName,
                        id: group.competitionId,
                      }],
                    },
                  ]
                }
              />
            </div>
          </div>
          <div className="row with-margin-top-extra">
            <div className="col">
              <MatchPaymentsTable
                {...this.props.matchPayments}
                saveFilters={false}
                headerTitle="Match Payments"
                appReady={this.props.appReady}
                actions={this.props.actions}
                isLoading={this.props.matchPayments.isLoading}
                persistentFilterConfig={{
                  groupId: this.props.match.params.teamId,
                }}
                selectMatchPayment={this.props.actions.selectMatchPayment}
              />
            </div>
          </div>
        </div>
      </div>
    );
  }
}
